import React from 'react';
import { CheckCircle, AlertCircle } from 'lucide-react';

interface ExtractedProduct {
  name: string;
  brand?: string | null;
  category?: string | null;
  confidence: number;
  quantity?: number | null;
}

interface ProductExtractionResultsProps {
  products: ExtractedProduct[];
  imageUrl?: string;
  showImage?: boolean;
}

const ProductExtractionResults: React.FC<ProductExtractionResultsProps> = ({
  products,
  imageUrl,
  showImage = true
}) => {
  if (products.length === 0) {
    return (
      <div className="bg-gray-50 rounded-lg p-4 text-center">
        <AlertCircle className="w-8 h-8 text-gray-400 mx-auto mb-2" />
        <p className="text-sm text-gray-500">No products detected</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      {showImage && imageUrl && (
        <div className="aspect-video relative bg-gray-100">
          <img
            src={imageUrl}
            alt="Uploaded"
            className="w-full h-full object-cover"
          />
        </div>
      )}

      <div className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <CheckCircle className="w-4 h-4 text-emerald-500" />
          <h4 className="font-medium text-gray-800">
            Products Detected ({products.length})
          </h4>
        </div>

        <div className="space-y-2 max-h-48 overflow-y-auto">
          {products.map((product, idx) => (
            <div
              key={idx}
              className="flex items-center justify-between py-1.5 border-b border-gray-100 last:border-0"
            >
              <div className="flex-1 min-w-0">
                <span className="text-sm text-gray-800 truncate block">
                  {product.name}
                </span>
                {product.brand && (
                  <span className="text-xs text-gray-500">
                    {product.brand}
                  </span>
                )}
              </div>
              <span
                className={`text-xs px-2 py-0.5 rounded-full ml-2 ${
                  product.confidence >= 0.8
                    ? 'bg-emerald-50 text-emerald-700'
                    : product.confidence >= 0.5
                    ? 'bg-amber-50 text-amber-700'
                    : 'bg-gray-100 text-gray-600'
                }`}
              >
                {Math.round(product.confidence * 100)}%
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ProductExtractionResults;
